import {Observable} from 'rxjs';
import {UserInfo} from '../user-dashboard/shared/user-info.model';
import {HttpUtils} from '../shared/http-utils';
import {URL_BASE} from '../core/constants';
import {map, switchMap} from 'rxjs/operators';
import {BluechatUserCreation} from './models/BluechatUserCreation';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BluechatAgentCreation} from './models/BluechatAgentCreation';
import {BluechatCompany} from './models/BluechatCompany';
import {BluechatNoteAttachment} from './models/BluechatNoteCreation';
import {BluechatProcess} from './models/BluechatProcess';
import {BluechatTemplate} from './models/BluechatTemplate';
import {BluechatBulkNoteCreation} from './models/BluechatBulkNoteCreation';
import {BluechatFindCarrierByDeliveryNumber} from './models/BluechatFindCarrierByDeliveryNumber';
import {Carrier} from '../user-dashboard/shared/carrier.model';
import {CarrierManagerService} from '../user-dashboard/shared/carrier-manager.service';
import {MimeTypeUtils} from '../shared/mime-type-utils';

@Injectable()
export class BluechatService {
  static DATE_FORMAT = 'yyyy-MM-dd HH:mm';

  private BLUECHAT_URI = 'api/bluechat'

  public static getPodAcceptedTypes(): string[] {
    return [
      MimeTypeUtils.getMimeTypeByExtension('png'),
      MimeTypeUtils.getMimeTypeByExtension('jpeg'),
      MimeTypeUtils.getMimeTypeByExtension('pdf')
    ]
  }

  constructor(private http: HttpClient, private carrierManagerService: CarrierManagerService) {
  }

  public deduceFirstnameAndLastNameFromEmail(user: UserInfo): { firstname: string, lastname: string } {
    const firstname = this.getFirstName(user);
    const lastname = this.getLastName(user);
    return {
      firstname,
      lastname,
    };
  }

  public enableBluechatOnCarrierManager(userId: string, request: BluechatUserCreation): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.BLUECHAT_URI}/carrier-managers/${userId}/activation`;
    return this.http.post(url, request, { headers })
      .pipe(
        map((user: any) => UserInfo.fromDto(user))
      );
  }

  public enableBluechatOnChepManager(userId: string, request: BluechatAgentCreation): Observable<UserInfo> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.BLUECHAT_URI}/chep-managers/${userId}/activation`;
    return this.http.post(url, request, { headers })
      .pipe(
        map((user: any) => UserInfo.fromDto(user))
      );
  }

  public enableBluechatOnCarrier(carrierId: string, bluechatCarrierCode?: string, users?: UserInfo[]): Observable<BluechatCompany> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.BLUECHAT_URI}/carriers/${carrierId}/activation`;

    let params  = new HttpParams();

    if (bluechatCarrierCode && bluechatCarrierCode.trim() !== '') {
      params = params.set('bluechatCarrierCode', bluechatCarrierCode);
    }
    if (users && users.length > 0) {
      users.map(el => el.id).forEach(id => params = params.append('carrierManagersIds', id));
    }

    return this.http.post(url, {}, { headers, params })
      .pipe(
        map((sdc: any) => BluechatCompany.fromDto(sdc))
      );
  }

  public enableBluchatOnCarrierWithAllAgents(carrier: Carrier) {
    return this.carrierManagerService.getByCarrier(carrier.id).pipe(
      switchMap(users => this.enableBluechatOnCarrier(carrier.id, null, users))
    );
  }

  public getCompany(bluechatCarrierCode: string): Observable<BluechatCompany> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.BLUECHAT_URI}/carriers`;

    let params  = new HttpParams();
    params = params.set('bluechatCarrierCode', bluechatCarrierCode);

    return this.http.get(url, { headers, params })
      .pipe(
        map((sdc: any) => BluechatCompany.fromDto(sdc))
      );
  }

  public getSelfAssignedCompanies(): Observable<BluechatCompany[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.BLUECHAT_URI}/carriers/assigned/self`;

    return this.http.get(url, { headers })
      .pipe(
        map((result: any[]) => result.map(sdc => BluechatCompany.fromDto(sdc)))
      );
  }

  public getProcesses(bluechatCarrierCodes: string[]): Observable<{ [blueyonderCarrierCode: string]: BluechatProcess[] }> {
    const url = `${URL_BASE}/${this.BLUECHAT_URI}/domains`;
    const headers = HttpUtils.createHeaders();
    let params = new HttpParams()
    bluechatCarrierCodes.forEach(bluechatCarrierCode => params = params.append('bluechatCarrierCodes', bluechatCarrierCode));

    return this.http.get<{ [blueyonderCarrierCode: string]: BluechatProcess[] }>(url, { headers, params });
  }

  public getSelfAssignedProcesses(): Observable<BluechatProcess[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/${this.BLUECHAT_URI}/domains/assigned/self`;

    return this.http.get(url, { headers })
      .pipe(
        map((result: any[]) => result.map(p => BluechatProcess.fromDto(p)))
      );
  }

  public getTemplates(process: string, noteType: string): Observable<BluechatTemplate[]> {
    const url = `${URL_BASE}/${this.BLUECHAT_URI}/templates`;
    const headers = HttpUtils.createHeaders();

    let params = new HttpParams()

    if (process) {
      params = params.set('domainTitle', process);
    }
    if (noteType) {
      params = params.set('categoryTitle', noteType);
    }

    return this.http.get<BluechatTemplate[]>(url, { headers, params }).pipe(
      map((result: any[]) => result.map(el => BluechatTemplate.fromDto(el)))
    );
  }

  public createBulk(bulkRequest: BluechatBulkNoteCreation): Observable<void> {
    const url = `${URL_BASE}/${this.BLUECHAT_URI}/notes/bulk`
    const headers = HttpUtils.createHeaders();

    return this.http.post<void>(url, bulkRequest, { headers });
  }

  public getBluchatFindByDeliveryNumbersLoad(deliveryNumbers: string[]): Observable<BluechatFindCarrierByDeliveryNumber[]> {
    const url = `${URL_BASE}/${this.BLUECHAT_URI}/carriers/find/by/deliveryNumbers`;
    const headers = HttpUtils.createHeaders();

    return this.http.post(url, deliveryNumbers, { headers }).pipe(
      map((object: any[]) => object.map(el => BluechatFindCarrierByDeliveryNumber.fromDto(el)))
    );
  }

  /** utils **/
  public fileToAttachment(files: File[]): BluechatNoteAttachment[] {
    return files.map(el => {
      const res = new BluechatNoteAttachment();
      res.mime_type = el.type;
      res.filename = el.name;
      el.arrayBuffer().then(arrayBuffer => res.data = btoa(this.arrayBufferToString(arrayBuffer)));
      return res;
    })
  };

  private arrayBufferToString(buffer: ArrayBuffer): string {
    let binaryString = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binaryString += String.fromCharCode(bytes[i]);
    }
    return binaryString;
  }

  private getFirstName(user: UserInfo): string {
    const email = user.email.split('@')[0]
    return email.includes('.') ? email.split('.')[0] : email;
  }

  private getLastName(user: UserInfo): string {
    const email = user.email.split('@')[0]
    return email.includes('.') ? email.split('.')[1] : email;
  }
}
